import { useState } from "react";
import { Button } from "@/components/ui/button";
import { 
  Palmtree, Mountain, Building2, Compass, Theater, Sparkles,
  UtensilsCrossed, Camera, Landmark, Music, TreePine, Waves,
  Heart, Users, User, Wallet, Crown, Plane,
  Globe, MapPin, Sun, Snowflake, ChevronRight, Check
} from "lucide-react";

const INTEREST_CATEGORIES = [
  {
    id: "travel_style",
    title: "How do you like to travel?",
    subtitle: "Select all that apply",
    options: [
      { id: "beach", label: "Beach & Islands", icon: Palmtree, color: "bg-cyan-500" },
      { id: "mountains", label: "Mountains", icon: Mountain, color: "bg-emerald-600" },
      { id: "city", label: "City Breaks", icon: Building2, color: "bg-slate-600" },
      { id: "adventure", label: "Adventure", icon: Compass, color: "bg-orange-500" },
      { id: "cultural", label: "Cultural", icon: Theater, color: "bg-purple-500" },
      { id: "relaxation", label: "Relaxation", icon: Sparkles, color: "bg-pink-400" },
    ]
  },
  {
    id: "activities",
    title: "What activities excite you?",
    subtitle: "Pick your favorites",
    options: [
      { id: "food_wine", label: "Food & Wine", icon: UtensilsCrossed, color: "bg-red-500" },
      { id: "photography", label: "Photography", icon: Camera, color: "bg-indigo-500" },
      { id: "history", label: "History", icon: Landmark, color: "bg-amber-600" },
      { id: "nightlife", label: "Nightlife", icon: Music, color: "bg-fuchsia-500" },
      { id: "nature", label: "Nature & Wildlife", icon: TreePine, color: "bg-green-600" },
      { id: "water_sports", label: "Water Sports", icon: Waves, color: "bg-blue-500" },
    ]
  },
  {
    id: "trip_type",
    title: "Who do you usually travel with?",
    subtitle: "Select your typical travel style",
    options: [
      { id: "romantic", label: "Romantic Getaways", icon: Heart, color: "bg-rose-500" },
      { id: "family", label: "Family Trips", icon: Users, color: "bg-teal-500" },
      { id: "solo", label: "Solo Adventures", icon: User, color: "bg-violet-500" },
      { id: "friends", label: "Friends Trip", icon: Users, color: "bg-sky-500" },
      { id: "budget", label: "Budget Travel", icon: Wallet, color: "bg-lime-600" },
      { id: "luxury", label: "Luxury Escapes", icon: Crown, color: "bg-yellow-500" },
    ]
  },
  {
    id: "regions",
    title: "Dream destinations?",
    subtitle: "Where would you love to explore?",
    options: [
      { id: "europe", label: "Europe", icon: Landmark, color: "bg-blue-600" },
      { id: "asia", label: "Asia", icon: Sun, color: "bg-orange-500" },
      { id: "americas", label: "Americas", icon: MapPin, color: "bg-red-600" },
      { id: "africa", label: "Africa", icon: TreePine, color: "bg-amber-700" },
      { id: "oceania", label: "Oceania", icon: Waves, color: "bg-cyan-600" },
      { id: "arctic", label: "Arctic/Antarctic", icon: Snowflake, color: "bg-slate-400" },
    ]
  }
];

export const InterestsOnboarding = ({ isOpen, onComplete, onSkip }) => {
  const [currentCategory, setCurrentCategory] = useState(0);
  const [selections, setSelections] = useState({
    travel_style: [],
    activities: [],
    trip_type: [],
    regions: []
  });
  const [isAnimating, setIsAnimating] = useState(false);

  if (!isOpen) return null;

  const category = INTEREST_CATEGORIES[currentCategory];
  const isLastCategory = currentCategory === INTEREST_CATEGORIES.length - 1;
  const currentSelections = selections[category.id] || [];

  const toggleSelection = (optionId) => {
    setSelections(prev => {
      const current = prev[category.id] || [];
      if (current.includes(optionId)) {
        return { ...prev, [category.id]: current.filter(id => id !== optionId) };
      } else {
        return { ...prev, [category.id]: [...current, optionId] };
      }
    });
  };

  const handleNext = () => {
    if (isLastCategory) {
      onComplete(selections);
    } else {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentCategory(prev => prev + 1);
        setIsAnimating(false);
      }, 200);
    }
  };

  const handleBack = () => {
    if (currentCategory > 0) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentCategory(prev => prev - 1);
        setIsAnimating(false);
      }, 200);
    }
  };

  const progress = ((currentCategory + 1) / INTEREST_CATEGORIES.length) * 100;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-gradient-to-br from-background via-background to-primary/5">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-20 left-10 w-32 h-32 bg-primary/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-40 h-40 bg-secondary/20 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 w-48 h-48 bg-accent/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
      </div>

      <div className="relative w-full max-w-lg mx-4">
        {/* Progress Bar */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-muted-foreground">
              Step {currentCategory + 1} of {INTEREST_CATEGORIES.length}
            </span>
            <button
              type="button"
              onClick={onSkip}
              className="text-sm text-muted-foreground hover:text-charcoal transition-colors"
            >
              Skip for now
            </button>
          </div>
          <div className="h-2 bg-charcoal/10 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-primary to-secondary transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Card */}
        <div 
          className={`bg-card rounded-3xl shadow-2xl overflow-hidden transform transition-all duration-300 ${
            isAnimating ? "scale-95 opacity-0" : "scale-100 opacity-100"
          }`}
        >
          {/* Header */}
          <div className="p-6 pb-4 text-center">
            <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center">
              <Plane className="w-7 h-7 text-white" />
            </div>
            <h2 className="text-2xl font-serif font-semibold text-charcoal mb-1">
              {category.title}
            </h2>
            <p className="text-muted-foreground text-sm">
              {category.subtitle}
            </p>
          </div>

          {/* Options Grid */}
          <div className="px-6 pb-6">
            <div className="grid grid-cols-2 gap-3">
              {category.options.map((option) => {
                const Icon = option.icon;
                const isSelected = currentSelections.includes(option.id);
                
                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => toggleSelection(option.id)}
                    className={`relative p-4 rounded-2xl border-2 transition-all duration-200 text-left group ${
                      isSelected 
                        ? "border-primary bg-primary/5 shadow-md" 
                        : "border-charcoal/10 hover:border-charcoal/20 hover:bg-charcoal/5"
                    }`}
                  >
                    {/* Selection Check */}
                    {isSelected && (
                      <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                    
                    {/* Icon */}
                    <div className={`w-10 h-10 rounded-xl ${option.color} flex items-center justify-center mb-2 transition-transform group-hover:scale-105`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    
                    {/* Label */}
                    <span className={`text-sm font-medium ${isSelected ? "text-charcoal" : "text-charcoal/80"}`}>
                      {option.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Actions */}
          <div className="px-6 pb-6 flex gap-3">
            {currentCategory > 0 && (
              <Button
                type="button"
                variant="outline"
                onClick={handleBack}
                className="flex-1 rounded-full border-charcoal/20"
              >
                Back
              </Button>
            )}
            <Button
              type="button"
              onClick={handleNext}
              disabled={currentSelections.length === 0}
              className={`flex-1 rounded-full btn-press ${
                currentSelections.length === 0 
                  ? "bg-charcoal/20 text-charcoal/40" 
                  : "bg-primary text-white hover:bg-primary/90"
              }`}
            >
              {isLastCategory ? (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Get Personalized Ideas
                </>
              ) : (
                <>
                  Continue
                  <ChevronRight className="w-4 h-4 ml-1" />
                </>
              )}
            </Button>
          </div>

          {/* Selection Count */}
          <div className="px-6 pb-4 text-center">
            <span className="text-xs text-muted-foreground">
              {currentSelections.length === 0 
                ? "Select at least one option" 
                : `${currentSelections.length} selected`}
            </span>
          </div>
        </div>

        {/* Category Dots */}
        <div className="flex justify-center gap-2 mt-6">
          {INTEREST_CATEGORIES.map((_, idx) => (
            <div
              key={idx}
              className={`h-2 rounded-full transition-all duration-300 ${
                idx === currentCategory 
                  ? "w-8 bg-primary" 
                  : idx < currentCategory 
                    ? "w-2 bg-secondary" 
                    : "w-2 bg-charcoal/20"
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default InterestsOnboarding;
